import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, FileText, Building2, Phone, Mail, MapPin } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const SECTIONS = [
  {
    n: "01",
    t: "Angaben gemäß § 5 TMG",
    body: (
      <p>
        Die folgenden Angaben werden gemäß § 5 Telemediengesetz (TMG)
        bereitgestellt und kennzeichnen den Diensteanbieter dieser Website.
      </p>
    ),
  },
  {
    n: "02",
    t: "Kontakt",
    body: (
      <p>
        Bei Fragen, Anliegen oder rechtlichen Hinweisen zu dieser Website
        erreichen Sie uns am schnellsten telefonisch oder per E-Mail.
      </p>
    ),
  },
  {
    n: "03",
    t: "Unternehmensgegenstand / Tätigkeit",
    body: (
      <p>
        Reinigungsdienstleistungen — insbesondere Gebäudereinigung,
        Treppenhausreinigung, Glas- und Rahmenreinigung sowie Winterdienst
        für private und gewerbliche Auftraggeber.
      </p>
    ),
  },
  {
    n: "04",
    t: "Umsatzsteuerliche Angaben",
    body: (
      <>
        <p>
          Steuernummer:{" "}
          <span className="font-semibold text-slate-900">
            11 103 238 82172 57 2544
          </span>
        </p>
        <p className="mt-4 text-sm text-slate-500">
          Bitte vor der finalen Veröffentlichung mit dem Original­dokument
          abgleichen.
        </p>
      </>
    ),
  },
  {
    n: "05",
    t: "Redaktionell verantwortlich",
    body: (
      <p>
        Verantwortlich für den Inhalt nach § 18 Abs. 2 Medienstaatsvertrag
        (MStV):
        <br />
        <span className="font-semibold text-slate-900">Kristian Kola</span>
        <br />
        Augsburgerstr. 26, 86157 Augsburg
      </p>
    ),
  },
  {
    n: "06",
    t: "EU-Streitschlichtung",
    body: (
      <>
        <p>
          Die Europäische Kommission stellt eine Plattform zur
          Online-Streitbeilegung (OS) bereit:{" "}
          <a
            href="https://ec.europa.eu/consumers/odr/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sky-700 font-medium hover:text-sky-900 underline decoration-sky-300 underline-offset-4"
          >
            ec.europa.eu/consumers/odr
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </p>
        <p className="mt-4">
          Unsere E-Mail-Adresse finden Sie oben im Impressum.
        </p>
      </>
    ),
  },
  {
    n: "07",
    t: "Verbraucherstreitbeilegung / Universalschlichtungsstelle",
    body: (
      <p>
        Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren
        vor einer Verbraucherschlichtungsstelle teilzunehmen.
      </p>
    ),
  },
  {
    n: "08",
    t: "Haftung für Inhalte",
    body: (
      <>
        <p>
          Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte
          auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach
          §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet,
          übermittelte oder gespeicherte fremde Informationen zu überwachen oder
          nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit
          hinweisen.
        </p>
        <p className="mt-4">
          Verpflichtungen zur Entfernung oder Sperrung der Nutzung von
          Informationen nach den allgemeinen Gesetzen bleiben hiervon
          unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt
          der Kenntnis einer konkreten Rechtsverletzung möglich. Bei
          Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese
          Inhalte umgehend entfernen.
        </p>
      </>
    ),
  },
  {
    n: "09",
    t: "Haftung für Links",
    body: (
      <p>
        Unser Angebot enthält ggf. Links zu externen Websites Dritter, auf deren
        Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden
        Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten
        Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten
        verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der
        Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte
        waren zum Zeitpunkt der Verlinkung nicht erkennbar.
      </p>
    ),
  },
  {
    n: "10",
    t: "Urheberrecht",
    body: (
      <p>
        Die durch die Seitenbetreiber erstellten Inhalte und Werke auf dieser
        Website unterliegen dem deutschen Urheberrecht. Die Vervielfältigung,
        Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der
        Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des
        jeweiligen Autors bzw. Erstellers.
      </p>
    ),
  },
];

export default function Impressum() {
  return (
    <main data-testid="impressum-page" className="min-h-screen bg-white">
      <Navbar />

      {/* Hero header */}
      <section className="relative pt-36 md:pt-44 pb-12 md:pb-16 bg-gradient-to-b from-slate-50 via-white to-white border-b border-slate-100 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(#0f172a 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="pointer-events-none absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-sky-100/50 blur-3xl" />

        <div className="relative mx-auto max-w-4xl px-6 md:px-10">
          <Link
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-sky-700 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>

          <div className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-1.5 mb-6 shadow-sm">
            <FileText className="w-3.5 h-3.5 text-sky-600" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-700">
              Rechtliches
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] font-semibold tracking-tight leading-[1.02] text-slate-900">
            Impressum<span className="text-sky-500">.</span>
          </h1>
          <p className="mt-5 text-slate-600 text-base md:text-lg max-w-2xl leading-relaxed">
            Anbieterkennzeichnung gemäß § 5 TMG sowie weitere rechtliche
            Hinweise zu dieser Website.
          </p>
        </div>
      </section>

      {/* Anbieter-Card */}
      <section className="relative bg-white pt-12 md:pt-16">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-sky-50/40 to-white shadow-[0_30px_80px_-30px_rgba(2,132,199,0.2)] overflow-hidden">
            <div className="grid md:grid-cols-2 gap-px bg-slate-200">
              {/* Anbieter */}
              <div className="bg-white p-7 md:p-9">
                <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-700 mb-4">
                  <Building2 className="w-3.5 h-3.5" />
                  Anbieter
                </div>
                <div className="font-display text-2xl md:text-[26px] font-semibold text-slate-900 tracking-tight leading-tight">
                  Kristian Kola Gebäudereinigung
                </div>
                <div className="mt-5 flex items-start gap-3 text-[15px] text-slate-700 leading-relaxed">
                  <MapPin className="w-4 h-4 mt-1 text-sky-500 shrink-0" />
                  <div>
                    Augsburgerstr. 26<br />
                    86157 Augsburg<br />
                    Deutschland
                  </div>
                </div>
              </div>

              {/* Kontakt */}
              <div className="bg-white p-7 md:p-9">
                <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-700 mb-4">
                  <Phone className="w-3.5 h-3.5" />
                  Kontakt
                </div>
                <ul className="space-y-4 text-[15px]">
                  <li className="flex items-start gap-3">
                    <Phone className="w-4 h-4 mt-1 text-sky-500 shrink-0" />
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500 mb-0.5">Telefon</div>
                      <a
                        href="tel:+4917624458820"
                        data-testid="impressum-phone"
                        className="font-medium text-slate-900 hover:text-sky-700 transition-colors"
                      >
                        +49 176 2445 8820
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <Mail className="w-4 h-4 mt-1 text-sky-500 shrink-0" />
                    <div>
                      <div className="text-xs uppercase tracking-widest text-slate-500 mb-0.5">E-Mail</div>
                      <a
                        href="mailto:kristian.kola95@icloud.com"
                        data-testid="impressum-email"
                        className="font-medium text-slate-900 hover:text-sky-700 transition-colors break-all"
                      >
                        kristian.kola95@icloud.com
                      </a>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detail Sections */}
      <section className="relative py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          {/* Inline TOC */}
          <nav
            aria-label="Inhaltsverzeichnis"
            className="hidden md:block rounded-2xl border border-slate-200 bg-slate-50/50 p-6 mb-14"
          >
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 mb-4">
              Inhalt
            </div>
            <ol className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.n}>
                  <a
                    href={`#section-${s.n}`}
                    className="group inline-flex items-baseline gap-3 text-slate-700 hover:text-sky-700 transition-colors"
                  >
                    <span className="font-display text-xs text-slate-400 group-hover:text-sky-600 tracking-widest">
                      {s.n}
                    </span>
                    <span className="border-b border-transparent group-hover:border-sky-300">
                      {s.t}
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {/* Sections */}
          <div className="space-y-14 md:space-y-16">
            {SECTIONS.map((s) => (
              <article
                key={s.n}
                id={`section-${s.n}`}
                className="scroll-mt-32"
              >
                <div className="flex items-baseline gap-5 mb-4">
                  <span className="font-display text-3xl md:text-4xl font-semibold tracking-tight bg-gradient-to-br from-slate-900 via-slate-700 to-sky-600 bg-clip-text text-transparent shrink-0">
                    {s.n}
                  </span>
                  <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900 tracking-tight leading-snug">
                    {s.t}
                  </h2>
                </div>
                <div className="ml-0 md:ml-[60px] max-w-none text-[15px] md:text-base text-slate-700 leading-relaxed [&_p]:mb-0">
                  {s.body}
                </div>
              </article>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-20 pt-10 border-t border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
            <p className="text-sm text-slate-600 max-w-md">
              Datenschutzerklärung lesen?{" "}
              <Link
                to="/datenschutz"
                className="font-semibold text-sky-700 hover:text-sky-900 underline decoration-sky-300 underline-offset-4"
              >
                Zum Datenschutz
              </Link>
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-600 text-white px-5 py-3 text-sm font-semibold transition-colors shadow-lg shadow-slate-900/10"
            >
              <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
